import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import {
  LayoutDashboard,
  Users,
  BedDouble,
  Gavel,
  Heart,
  BarChart3,
  HandHelping,
  Calendar,
  Settings,
  ChevronDown,
  Phone,
  Baby,
  UserCheck,
  ClipboardList,
  ListOrdered,
  Shield,
  Building2,
  Wrench,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useAppStore } from "@/lib/store";

interface NavItem {
  label: string;
  href?: string;
  icon: React.ElementType;
  children?: NavItem[];
  badge?: number;
}

const navigation: NavItem[] = [
  {
    label: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    label: "Clients",
    icon: Users,
    children: [
      { label: "All Clients", href: "/clients", icon: Users },
      { label: "Adult Shelter", href: "/clients?type=adult-shelter", icon: UserCheck },
      { label: "Child Shelter", href: "/clients?type=child-shelter", icon: Baby },
      { label: "Outreach", href: "/clients?type=outreach", icon: HandHelping },
      { label: "Hotline", href: "/clients?type=hotline", icon: Phone },
    ],
  },
  {
    label: "Case Management",
    href: "/cases",
    icon: ClipboardList,
  },
  {
    label: "Shelter",
    icon: BedDouble,
    children: [
      { label: "Bed Board", href: "/shelter", icon: BedDouble },
      { label: "Waiting List", href: "/shelter/waiting-list", icon: ListOrdered },
    ],
  },
  {
    label: "Legal / Court",
    href: "/legal",
    icon: Gavel,
  },
  {
    label: "Sexual Assault",
    href: "/sexual-assault",
    icon: Heart,
  },
  {
    label: "Reports",
    href: "/reports",
    icon: BarChart3,
  },
  {
    label: "Volunteers",
    href: "/volunteers",
    icon: HandHelping,
  },
  {
    label: "Events",
    href: "/events",
    icon: Calendar,
  },
  {
    label: "Messages",
    href: "/messaging",
    icon: Mail,
  },
  {
    label: "Admin",
    icon: Settings,
    children: [
      { label: "Users", href: "/admin/users", icon: Shield },
      { label: "Agencies", href: "/admin/agencies", icon: Building2 },
      { label: "Configuration", href: "/admin/config", icon: Wrench },
    ],
  },
];

export function Sidebar() {
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);

  return (
    <aside
      className={cn(
        "flex h-full flex-col bg-gradient-to-b from-sidebar via-sidebar to-[color-mix(in_srgb,var(--sidebar)_85%,black)] text-sidebar-foreground transition-all duration-300",
        sidebarCollapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center border-b border-sidebar-border/50 px-4">
        {!sidebarCollapsed ? (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md shadow-indigo-500/20">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold tracking-wide text-white">
                FVPS
              </h1>
              <p className="text-[10px] font-medium uppercase tracking-widest text-sidebar-foreground/50">
                Case Mgmt
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md shadow-indigo-500/20">
            <Shield className="h-5 w-5 text-white" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {navigation.map((item) => (
            <SidebarItem
              key={item.label}
              item={item}
              collapsed={sidebarCollapsed}
            />
          ))}
        </ul>
      </nav>

      {/* Footer */}
      {!sidebarCollapsed && (
        <div className="border-t border-sidebar-border/30 px-4 py-3">
          <p className="text-[10px] text-sidebar-foreground/30 text-center">
            v2.0 &middot; FVPS CMS
          </p>
        </div>
      )}
    </aside>
  );
}

function SidebarItem({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    if (!item.children) return false;
    return item.children.some(
      (child) => child.href && location.pathname.startsWith(child.href.split("?")[0]!),
    );
  });

  const Icon = item.icon;

  // No children - simple link
  if (!item.children && item.href) {
    return (
      <li>
        <NavLink
          to={item.href}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
              "hover:bg-white/[0.08] hover:text-white",
              isActive
                ? "bg-white/[0.12] text-white shadow-sm"
                : "text-sidebar-foreground/70",
              collapsed && "justify-center px-2",
            )
          }
          title={collapsed ? item.label : undefined}
        >
          {({ isActive }) => (
            <>
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
                  isActive
                    ? "bg-gradient-to-br from-indigo-500/30 to-purple-500/30 text-indigo-300"
                    : "text-sidebar-foreground/50 group-hover:text-indigo-300",
                  collapsed && "h-9 w-9",
                )}
              >
                <Icon className={cn("h-[18px] w-[18px]", collapsed && "h-5 w-5")} />
              </div>
              {!collapsed && <span>{item.label}</span>}
            </>
          )}
        </NavLink>
      </li>
    );
  }

  // Has children - collapsible section
  if (collapsed) {
    const firstHref = item.children?.[0]?.href ?? "#";
    return (
      <li>
        <NavLink
          to={firstHref}
          className={cn(
            "group flex items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium transition-all duration-150",
            "hover:bg-white/[0.08] hover:text-white text-sidebar-foreground/70",
          )}
          title={item.label}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-md text-sidebar-foreground/50 group-hover:text-indigo-300 transition-colors">
            <Icon className="h-5 w-5" />
          </div>
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
          "hover:bg-white/[0.08] hover:text-white text-sidebar-foreground/70",
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/50 group-hover:text-indigo-300 transition-colors">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <span className="flex-1 text-left">{item.label}</span>
        <div className={cn(
          "transition-transform duration-200",
          open && "rotate-180"
        )}>
          <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/40" />
        </div>
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-200",
        open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      )}>
        <ul className="ml-6 mt-1 space-y-0.5 border-l border-sidebar-border/30 pl-3">
          {item.children!.map((child) => (
            <li key={child.label}>
              <NavLink
                to={child.href ?? "#"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] transition-all duration-150",
                    "hover:bg-white/[0.06] hover:text-white",
                    isActive
                      ? "text-indigo-300 font-medium bg-white/[0.05]"
                      : "text-sidebar-foreground/50",
                  )
                }
              >
                <child.icon className="h-3.5 w-3.5 shrink-0" />
                <span>{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
